import { EnvelopeSimpleIcon, PlusIcon } from "@phosphor-icons/react";
import { Button, Card, EmptyState, PageHeader, Section, SkeletonRows } from "@bafe/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { AccountStorageSummary } from "../../components/AccountStorageSummary";
import { ErrorPanel } from "../../components/ErrorPanel";
import { LinkButton } from "../../components/LinkButton";
import { MailboxTable } from "../../components/MailboxTable";
import { QueryState } from "../../components/QueryState";
import { api } from "../../lib/api";
import { queryKeys } from "../../lib/query";

export function MailboxesList() {
	const storage = useQuery({ queryKey: queryKeys.storage, queryFn: () => api.getStorage() });

	const mailboxes = useInfiniteQuery({
		queryKey: queryKeys.mailboxesPaged(),
		queryFn: ({ pageParam }) => api.listMailboxes({ cursor: pageParam, limit: 50 }),
		initialPageParam: null as string | null,
		getNextPageParam: (lastPage) => lastPage.meta.nextCursor,
		refetchOnMount: "always",
		refetchInterval: 60_000,
		refetchIntervalInBackground: true,
	});

	const rows = mailboxes.data?.pages.flatMap((page) => page.data) ?? [];

	return (
		<div className="flex flex-col gap-6">
			<PageHeader
				title="Mailboxes"
				description="Every email account across your domains."
				action={
					<LinkButton
						to="/manage/email/mailboxes/new"
						variant="primary"
						iconLeft={<PlusIcon className="size-4" aria-hidden="true" />}
					>
						Create mailbox
					</LinkButton>
				}
			/>

			<Card>
				<QueryState
					query={storage}
					loading={<SkeletonRows rows={1} />}
					errorTitle="We could not load your storage."
				>
					{(data) => (
						<div className="flex flex-col gap-2">
							<AccountStorageSummary storage={data} />
							<p className="max-w-prose text-xs text-(--text-muted)">
								Your plan is one shared storage pool. Email accounts and domains are counted here for
								information only — they are not charged separately.
							</p>
						</div>
					)}
				</QueryState>
			</Card>

			<Section>
				{mailboxes.isPending && <SkeletonRows rows={6} />}

				{mailboxes.isError && (
					<ErrorPanel
						error={mailboxes.error}
						title="We could not load your email accounts."
						onRetry={() => void mailboxes.refetch()}
						retryPending={mailboxes.isFetching}
					/>
				)}

				{mailboxes.isSuccess && (
					<>
						<Card flush>
							<MailboxTable
								caption="Your email accounts"
								rows={rows}
								empty={
									<EmptyState
										icon={<EnvelopeSimpleIcon className="size-8" aria-hidden="true" />}
										title="No email accounts yet"
										description="Create your first address, such as hello@yourcompany.com. It works on the web and on any mail app."
										action={
											<LinkButton to="/manage/email/mailboxes/new" variant="primary">
												Create your first mailbox
											</LinkButton>
										}
									/>
								}
							/>
						</Card>

						{mailboxes.hasNextPage && (
							<div className="flex justify-center">
								<Button
									variant="secondary"
									pending={mailboxes.isFetchingNextPage}
									pendingLabel="Loading…"
									onClick={() => void mailboxes.fetchNextPage()}
								>
									Show more
								</Button>
							</div>
						)}
					</>
				)}
			</Section>
		</div>
	);
}
